import "./App.css";
import React, { useContext, useEffect } from "react";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import StarekContext from "./context/StarekContext";
import {
    AuthGuardedRoute,
    PlantChosenGuardedRoute,
    PermissionGuardedRoute,
} from "./components/GuardedRoutes";

import AppLayout from "./layouts/AppLayout";
import MonoLayout from "./layouts/MonoLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import Login from "./pages/Login";
import SSOLogin from "./pages/SSOLogin";

import ChoosePlant from "./pages/ChoosePlant";
import { Devices } from "./pages/device/Devices";
import { Zones } from "./pages/zone/Zones";
import { Users } from "./pages/user/Users";
import { Roles } from "./pages/role/Roles";
import { Shifts } from "./pages/shift/Shifts";
import { JobSchedules } from "./pages/jobSched/JobSchedules";
import { Maintenance } from "./pages/maintenanceSchedule/MaintenanceSchedule";
import { Settings } from "./pages/setting/Settings";
import { DeviceProfiles } from "./pages/deviceProfile/DeviceProfiles";
import { Parts } from "./pages/part/Parts";
import { Webhooks } from "./pages/webhook/Webhooks";
import { Cycles } from "./pages/cycle/Cycles";
import Reports from "./pages/report/Reports";
import FavReports from "./pages/report/FavReports";
import FabricDebug from "./pages/FabricDebug";
import SampleWizard from "./components/SampleWizard";

function StarekApp() {
    const starekContext = useContext(StarekContext);

    function parseJwt(token) {
        try {
            const base64Payload = token.split(".")[1];
            const jsonPayload = JSON.parse(
                typeof window === "undefined"
                    ? Buffer.from(base64Payload, "base64").toString("utf-8")
                    : atob(base64Payload)
            );
            return jsonPayload;
        } catch (e) {
            console.warn("[Auth Debug] Failed to parse JWT:", e);
            return null;
        }
    }

    return (
        <Router>
            <Routes>
                {/* Root redirect */}
                <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                />
                {/* Debug route */}
                <Route path="/debug/fabric" element={<FabricDebug />} />
                {/* Sample Wizard reference implementation */}
                <Route path="/debug/wizard-sample" element={<SampleWizard />} />
                {/* Protected routes */}
                <Route
                    path="/choose-plant"
                    element={
                        <AuthGuardedRoute>
                            <MonoLayout>
                                <ChoosePlant />
                            </MonoLayout>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/dashboard"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <AppLayout>
                                    <Dashboard />
                                </AppLayout>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/devices"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="device">
                                    <AppLayout>
                                        <Devices />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/zones"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="zone">
                                    <AppLayout>
                                        <Zones />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/zones/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="zone">
                                    <AppLayout>
                                        <Zones />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/users"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="user">
                                    <AppLayout>
                                        <Users />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/user/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="user">
                                    <AppLayout>
                                        <Users />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/reports"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="report">
                                    <AppLayout>
                                        <Reports />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/fav-reports"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="report">
                                    <AppLayout>
                                        <FavReports />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/roles"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="role">
                                    <AppLayout>
                                        <Roles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/role/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="role">
                                    <AppLayout>
                                        <Roles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/shifts"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="shift">
                                    <AppLayout>
                                        <Shifts />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/shifts/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="shift">
                                    <AppLayout>
                                        <Shifts />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/job-schedules"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="job_schedule">
                                    <AppLayout>
                                        <JobSchedules />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/job-schedule/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="job_schedule">
                                    <AppLayout>
                                        <JobSchedules />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/maintenance"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="job_schedule">
                                    <AppLayout>
                                        <Maintenance />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/settings"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="setting">
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/setting/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="setting">
                                    <AppLayout>
                                        <Settings />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/device-profiles"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="device_profile">
                                    <AppLayout>
                                        <DeviceProfiles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/device-profile/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="device_profile">
                                    <AppLayout>
                                        <DeviceProfiles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/parts"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="part">
                                    <AppLayout>
                                        <Parts />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/parts/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="part">
                                    <AppLayout>
                                        <Parts />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/webhooks"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="webhook">
                                    <AppLayout>
                                        <Webhooks />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/webhook/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="webhook">
                                    <AppLayout>
                                        <Webhooks />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/cycles"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="cycle">
                                    <AppLayout>
                                        <Cycles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                <Route
                    path="/cycle/:id"
                    element={
                        <AuthGuardedRoute>
                            <PlantChosenGuardedRoute>
                                <PermissionGuardedRoute entityCode="cycle">
                                    <AppLayout>
                                        <Cycles />
                                    </AppLayout>
                                </PermissionGuardedRoute>
                            </PlantChosenGuardedRoute>
                        </AuthGuardedRoute>
                    }
                />
                {/* Public routes */}
                <Route
                    path="/login"
                    element={
                        <MonoLayout>
                            <Login />
                        </MonoLayout>
                    }
                />
                <Route
                    path="/sso-authenticate"
                    element={
                        <MonoLayout>
                            <SSOLogin />
                        </MonoLayout>
                    }
                />
                {/* Add other public routes here */}
            </Routes>
        </Router>
    );
}

export default StarekApp;
